import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Tag, ArrowLeft, Phone, MessageCircle, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/blog/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Article non trouvé');
        } else {
          setError('Erreur lors du chargement de l\'article');
        }
        return;
      }
      
      const data = await response.json();
      setPost(data);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const phoneNumber = "0665976864";

  const handleCallClick = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Bonjour, je souhaite obtenir plus d'informations sur vos services.");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copier l'URL
        await navigator.clipboard.writeText(window.location.href);
        alert('Lien copié dans le presse-papier !');
      }
    } catch (error) {
      console.error('Erreur de partage:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 text-lg">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">😔 Oups !</h1>
          <p className="text-gray-600 text-lg mb-8">{error || 'Article non trouvé'}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {post && (
        <Helmet>
          <title>{`${post.title} - BeauClean Bordeaux`}</title>
          <meta name="description" content={post.excerpt} />
          <meta name="keywords" content={post.keywords.join(', ')} />
          <link rel="canonical" href={`https://beauclean33.fr/blog/${post.slug}`} />
          
          {/* Open Graph */}
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.excerpt} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://beauclean33.fr/blog/${post.slug}`} />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={post.excerpt} />
        </Helmet>
      )}

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header */}
        <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/blog" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
                <ArrowLeft className="w-5 h-5" />
                <span className="font-semibold">Retour au blog</span>
              </Link>
              <Link to="/" className="text-xl sm:text-2xl font-bold hover:opacity-90 transition-opacity">
                BeauClean
              </Link>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Image hero */}
          {post.image_url && (
            <div className="rounded-2xl overflow-hidden shadow-2xl mb-8">
              <img 
                src={post.image_url} 
                alt={post.title}
                className="w-full h-64 sm:h-96 object-cover"
              />
            </div>
          )}
          
          {/* En-tête de l'article */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
            {/* Catégorie */}
            <div className="mb-4">
              <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold ${
                post.category === 'auto'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {post.category === 'auto' ? '🚗 Auto' : '🛋️ Meubles'}
              </span>
            </div>

            {/* Titre */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Métadonnées */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-gray-600 mb-6 pb-6 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span>{formatDate(post.created_at)}</span>
              </div>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-medium">Partager</span>
              </button>
            </div>

            {/* Keywords */}
            <div className="flex flex-wrap gap-2">
              {post.keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  <Tag className="w-4 h-4" />
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          {/* Contenu */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10">
            <div className="prose prose-lg prose-blue max-w-none">
              <ReactMarkdown
                components={{
                  h2: ({node, ...props}) => <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                  li: ({node, ...props}) => <li className="text-base sm:text-lg" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                  a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* CTA Footer */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-6 sm:p-8 text-white text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Intéressé par nos services ?
            </h2>
            <p className="text-base sm:text-lg text-blue-100 mb-6">
              BeauClean intervient à Bordeaux et dans un rayon de 40km pour tous vos besoins de nettoyage
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleWhatsAppClick}
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-3 rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
              <button
                onClick={handleCallClick}
                className="flex items-center justify-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Phone className="w-5 h-5" />
                06 65 97 68 64
              </button>
            </div>
          </div>

          {/* Retour au blog */}
          <div className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Voir tous les articles
            </Link>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm">
              © 2024 BeauClean - Nettoyage automobile et meubles à Bordeaux
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/" className="hover:text-white transition-colors">
                Accueil
              </Link>
              <Link to="/meubles" className="hover:text-white transition-colors">
                Meubles
              </Link>
              <Link to="/blog" className="hover:text-white transition-colors">
                Blog
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPost;
